import dayjs from '@/plugins/dayjs'
import { createSeededRandom, pick, randomInt } from '@/utils/randomSeeded'
import { nextId } from '@/services/mock/idGenerator'
import type { Channel } from '@/types/common'
import type { Customer, ConsentSummary } from '@/types/customer'
import { FIRST_NAMES, LAST_NAMES, CITIES, ALL_COMPANIES, TAGS } from './romanian-data'

function stripDiacritics(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '')
}

function clampToNow(moment: dayjs.Dayjs): string {
  const now = dayjs()
  return (moment.isAfter(now) ? now : moment).toISOString()
}

function makePhone(random: () => number, usedPhones: Set<string>): string {
  let phone: string
  do {
    phone = `07${randomInt(10, 99, random)}${randomInt(100000, 999999, random)}`
  } while (usedPhones.has(phone))
  usedPhones.add(phone)
  return phone
}

function makeEmail(firstName: string, lastName: string, company: string | undefined, index: number): string {
  const local = stripDiacritics(`${firstName}.${lastName}`).toLowerCase()
  const domainBase = company ? stripDiacritics(company).toLowerCase().replace(/[^a-z0-9]+/g, '') : 'gmail'
  const domain = company ? `${domainBase}.ro` : 'gmail.com'
  return index > 0 ? `${local}${index}@${domain}` : `${local}@${domain}`
}

const CHANNEL_POOL: Channel[] = ['email', 'sms', 'whatsapp']

/**
 * Channels follow the consent, never the other way round: someone with no
 * accepted consent has no reachable channel at all. Generating the two
 * independently produced demo customers marked "Fără acord" who still carried
 * channel icons — and, once §7 landed, could be messaged.
 */
function pickChannels(random: () => number, consent: ConsentSummary): Channel[] {
  if (consent === 'none') return []
  const count = consent === 'partial' ? 1 : randomInt(2, 3, random)
  const shuffled = [...CHANNEL_POOL].sort(() => random() - 0.5)
  return shuffled.slice(0, count)
}

function pickTags(random: () => number): string[] {
  const count = randomInt(0, 3, random)
  const shuffled = [...TAGS].sort(() => random() - 0.5)
  return shuffled.slice(0, count)
}

/** Pads the hand-authored fixture list to a realistic volume for pagination/filtering demos. */
export function generateCustomers(count: number, seed = 42): Customer[] {
  const random = createSeededRandom(seed)
  const usedPhones = new Set<string>()
  const customers: Customer[] = []

  for (let i = 0; i < count; i++) {
    const firstName = pick(FIRST_NAMES, random)
    const lastName = pick(LAST_NAMES, random)
    const city = pick(CITIES, random)
    const hasCompany = random() < 0.35
    const company = hasCompany ? pick(ALL_COMPANIES, random) : undefined
    const createdDaysAgo = randomInt(2, 730, random)
    const createdAt = dayjs().subtract(createdDaysAgo, 'day')
    const hasInteracted = random() < 0.85
    const consentRoll = random()
    const consentSummary: ConsentSummary = consentRoll < 0.62 ? 'accepted' : consentRoll < 0.83 ? 'partial' : 'none'

    customers.push({
      id: nextId('cus'),
      firstName,
      lastName,
      phone: makePhone(random, usedPhones),
      email: makeEmail(firstName, lastName, company, i),
      company,
      jobTitle: hasCompany ? pick(['Manager', 'Proprietar', 'Asistent', 'Coordonator'], random) : undefined,
      city,
      status: random() < 0.06 ? 'archived' : 'active',
      channels: pickChannels(random, consentSummary),
      consentSummary,
      consentDate: consentSummary !== 'none' ? clampToNow(createdAt.add(randomInt(0, 3, random), 'day')) : undefined,
      tags: pickTags(random),
      lastInteractionAt: hasInteracted
        ? dayjs().subtract(randomInt(0, Math.min(createdDaysAgo, 120), random), 'day').toISOString()
        : undefined,
      createdAt: createdAt.toISOString(),
    })
  }

  return customers
}
